import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Disclosure, EmptyState, Label, Tag } from '../components/primitives';
import { GlossaryProvider, Glossed } from '../components/Glossary';
import { mutedFromReference } from '../data/review';
import { getMutedChecks, setMutedCheck, unmuteCheck } from '../data/store';
import { TAXONOMY, evidenceFor, type FailureMode } from '../data/taxonomy';
import { EVIDENCE_CASES, formatCaseDate } from '../data/evidence-cases';
import type { EvidenceCaseId } from '../data/evidence-cases';
import type { FailureModeId, MutedCheck, MutedChecks } from '../types';
import { formatRrpCategory } from '../data/rrp';
import { EVIDENCE_BADGES } from '../data/report';
import { MODEL_LAYER_FAILURE_MODES } from '../engine/model';
import { groupChecksByPlanArea, mutedSentence } from './checksModel';
import './Taxonomy.css';

type Tier = 1 | 2;
type Category = 'Financial' | 'Operational' | 'Performance' | 'cross-cutting';
type Layer = 'deterministic' | 'model-assisted';

/**
 * Tier is the one piece of internal vocabulary this page keeps, because the
 * distinction is real and a reviewer has to be able to weigh it. It never
 * appears without this sentence beside it.
 */
const TIER_MEANING: Record<Tier, string> = {
  1: 'a named federal audit records this exact cause producing a real dispute or dollar loss',
  2: 'FEMP’s own guidance warns about it, but no audit case is attached yet',
};

/** How each check is run. Labels are the reader's; these keys are the engine's. */
const LAYER_LABELS: Record<Layer, string> = {
  deterministic: 'Arithmetic',
  'model-assisted': 'Pattern match',
};

/** Counts a reader meets in a sentence rather than in a table. */
const NUMBER_WORDS = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
];

function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

/**
 * How a check is run. Derived from the pattern-match layer's own list, so the
 * two can never disagree: anything it does not own is checked by arithmetic.
 */
function layerOf(id: string): Layer {
  return (MODEL_LAYER_FAILURE_MODES as string[]).includes(id)
    ? 'model-assisted'
    : 'deterministic';
}

function categoryOf(fm: FailureMode): Category {
  const raw = fm.rrp_category.trim();
  if (/^financial/i.test(raw)) return 'Financial';
  if (/^operational/i.test(raw)) return 'Operational';
  if (/^performance/i.test(raw)) return 'Performance';
  return 'cross-cutting';
}

const CATEGORIES: Category[] = ['Financial', 'Operational', 'Performance', 'cross-cutting'];
const LAYERS: Layer[] = ['deterministic', 'model-assisted'];
const TIERS: Tier[] = [1, 2];

function FilterGroup<T extends string | number>({
  label,
  options,
  selected,
  onToggle,
  counts,
  renderOption,
}: {
  label: string;
  options: T[];
  selected: Set<T>;
  onToggle(value: T): void;
  counts: Map<T, number>;
  renderOption?(value: T): string;
}) {
  return (
    <div className="tx__filter-group">
      <Label>{label}</Label>
      <div className="tx__filter-row">
        {options.map((option) => {
          const on = selected.has(option);
          return (
            <button
              key={String(option)}
              type="button"
              className={`tx__filter${on ? ' tx__filter--on' : ''}`}
              aria-pressed={on}
              onClick={() => onToggle(option)}
            >
              {renderOption ? renderOption(option) : String(option)}
              <span className="tx__filter-count">{counts.get(option) ?? 0}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EvidenceLinks({ id }: { id: FailureModeId }) {
  const cases = evidenceFor(id)
    .map((caseId) => EVIDENCE_CASES[caseId as EvidenceCaseId])
    .filter(Boolean);

  if (cases.length === 0) {
    return (
      <p className="tx__evidence-none">
        No audit case is mapped to this check. Its ground truth is recomputation:
        the figures are in the document and the arithmetic settles it.
      </p>
    );
  }

  return (
    <ul className="tx__evidence">
      {cases.map((c) => (
        <li key={c.id}>
          <a href={c.url} target="_blank" rel="noreferrer noopener">
            {/* The issuer sits beside this on the same row, so the number alone
                is unambiguous here; the label covers the standards, which have
                no number. */}
            <span className="tx__evidence-number">{c.reportNumber ?? c.shortLabel}</span>
          </a>
          <span className="tx__evidence-meta">
            {c.issuer} · {formatCaseDate(c.date)} · {c.scale}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The one control on this page that changes what a reviewer will see in a
 * review. It is a switch rather than two buttons, because muting and unmuting
 * are one state, and it says what it does in both positions rather than only
 * in the one the reviewer is not in.
 */
function MuteToggle({
  fm,
  muted,
  onToggle,
}: {
  fm: FailureMode;
  muted?: MutedCheck;
  onToggle(id: FailureModeId, next: boolean): void;
}) {
  const on = muted !== undefined;

  return (
    <div className={`tx__mute${on ? ' tx__mute--on' : ''}`}>
      <button
        type="button"
        className="tx__mute-switch"
        role="switch"
        aria-checked={on}
        onClick={() => onToggle(fm.id, !on)}
      >
        <span className="tx__mute-box" aria-hidden="true">
          {on ? '✓' : ''}
        </span>
        <span>Muted in my reviews</span>
      </button>
      <p className="tx__mute-note">
        {on
          ? `${mutedSentence(muted)} It still runs on every review: its findings go to a collapsed “Muted checks” group at the bottom of the list rather than into the plan areas, and nothing about them is deleted.`
          : 'This check runs on every review and its findings appear in the list. Muting it here moves them to a collapsed “Muted checks” group instead. Muting is per browser, and reversible from this switch.'}
      </p>
    </div>
  );
}

/**
 * Everything a row says when it is opened: what the check is, what it looks
 * for, what it cannot catch, the matrix row it belongs to, the audits behind
 * it, and the switch that quiets it.
 *
 * Exported because a closed disclosure renders none of its children, so this
 * is the only way tools/pages-check.tsx can read what a row actually says.
 */
export function CheckDetail({
  fm,
  muted,
  onToggleMute,
}: {
  fm: FailureMode;
  muted?: MutedCheck;
  onToggleMute(id: FailureModeId, next: boolean): void;
}) {
  return (
    <>
      <div className="tx__field">
        <Label>What it is</Label>
        <p className="tx__prose">
          <Glossed>{fm.plain_description}</Glossed>
        </p>
      </div>

      <div className="tx__field">
        <Label>What it looks for</Label>
        <p className="tx__prose">
          <Glossed>{fm.plainSignal}</Glossed>
        </p>
        {/* The published signals themselves, kept under the sentence that
            explains them: they are what the engine matches on and what a
            finding states as its basis, so a paraphrase cannot replace
            them. */}
        <ul className="tx__signals">
          {fm.detection_signal.map((signal, i) => (
            <li key={i}>
              <Glossed>{signal}</Glossed>
            </li>
          ))}
        </ul>
        <p className="tx__prose tx__prose--mono tx__where">
          <Glossed>{fm.where_it_appears}</Glossed>
        </p>
      </div>

      <div className="tx__field">
        <Label>What it cannot catch</Label>
        <p className="tx__prose">
          <Glossed>{fm.cannotCatch}</Glossed>
        </p>
      </div>

      <div className="tx__field">
        <Label>Why it produces a dispute</Label>
        <p className="tx__prose">
          <Glossed>{fm.why_it_produces_dispute}</Glossed>
        </p>
      </div>

      <div className="tx__field">
        <Label>RRP Matrix row</Label>
        <p className="tx__prose tx__prose--mono">
          <Glossed>{formatRrpCategory(fm.rrp_category)}</Glossed>
        </p>
      </div>

      <div className="tx__field">
        <Label>Where the evidence comes from</Label>
        <p className="tx__prose tx__prose--mono">
          <Glossed>{fm.ground_truth}</Glossed>
        </p>
        <EvidenceLinks id={fm.id} />
      </div>

      {fm.note ? (
        <div className="tx__note">
          <Label tone="amber">Note</Label>
          <p className="tx__prose">
            <Glossed>{fm.note}</Glossed>
          </p>
        </div>
      ) : null}

      <MuteToggle fm={fm} muted={muted} onToggle={onToggleMute} />
    </>
  );
}

function Row({
  fm,
  muted,
  onToggleMute,
}: {
  fm: FailureMode;
  muted?: MutedCheck;
  onToggleMute(id: FailureModeId, next: boolean): void;
}) {
  const [open, setOpen] = useState(false);
  const layer = layerOf(fm.id);

  return (
    <li className={`tx__row${open ? ' tx__row--open' : ''}`}>
      <Disclosure
        open={open}
        onOpenChange={setOpen}
        headClassName="tx__head"
        panelClassName="tx__body"
        summary={
          <>
            {/* The plain title leads. The catalogue's own name sits under it,
                and the code is mono metadata at the end of the row. */}
            <span className="tx__name">
              <span className="tx__plain">{fm.plainTitle}</span>
              <span className="tx__internal">{fm.name}</span>
            </span>
            <span className="tx__tags">
              {muted ? (
                <Tag
                  tone="amber"
                  sans
                  title="You asked Ceiling to stop surfacing this check by default. It still runs."
                >
                  Muted
                </Tag>
              ) : null}
              <Tag sans title={`Tier ${fm.tier} — ${TIER_MEANING[fm.tier as Tier]}`}>
                Tier {fm.tier}
              </Tag>
              <Tag
                tone={layer === 'deterministic' ? 'blue' : 'amber'}
                sans
                title={
                  layer === 'deterministic'
                    ? EVIDENCE_BADGES.arithmetic.definition
                    : 'Found by matching the published detection signals against the plan text, then checked against the quote.'
                }
              >
                {LAYER_LABELS[layer]}
              </Tag>
            </span>
            <span className="tx__rrp">{formatRrpCategory(fm.rrp_category)}</span>
            <span className="tx__id">{fm.id}</span>
          </>
        }
      >
        <CheckDetail fm={fm} muted={muted} onToggleMute={onToggleMute} />
      </Disclosure>
    </li>
  );
}

export default function Taxonomy() {
  const modes = TAXONOMY.failure_modes;

  const [tiers, setTiers] = useState<Set<Tier>>(new Set());
  const [categories, setCategories] = useState<Set<Category>>(new Set());
  const [layers, setLayers] = useState<Set<Layer>>(new Set());

  /* --- the checks this browser has been asked to quiet --------------------
     Muting happens in a review, when the same check has been set aside three
     times; it is also chosen here, in advance, by a reviewer who already knows
     the check does not apply to their work. Either way it is undone here,
     because this is the page that lists every check and is the only place a
     reviewer would think to look for one that stopped appearing. */

  const [muted, setMuted] = useState<MutedChecks>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await getMutedChecks();
        if (!cancelled) setMuted(stored);
      } catch {
        // Nothing is muted, which is the honest default.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMute = useCallback((id: FailureModeId, next: boolean) => {
    // The switch answers immediately and the write follows it. A reviewer
    // pressing it must not watch a round trip to IndexedDB, and a browser with
    // storage blocked must still be able to quiet a check for this session.
    if (next) {
      const entry = mutedFromReference(id);
      setMuted((current) => ({ ...current, [id]: entry }));
      void setMutedCheck(entry)
        .then(setMuted)
        .catch(() => undefined);
      return;
    }
    setMuted((current) => {
      const rest = { ...current };
      delete rest[id];
      return rest;
    });
    void unmuteCheck(id)
      .then(setMuted)
      .catch(() => undefined);
  }, []);

  const mutedList = useMemo(
    () => modes.filter((fm) => muted[fm.id] !== undefined),
    [modes, muted],
  );

  // Every count on this page is computed from the taxonomy, never written down.
  const counts = useMemo(() => {
    const tierCounts = new Map<Tier, number>();
    const categoryCounts = new Map<Category, number>();
    const layerCounts = new Map<Layer, number>();

    for (const fm of modes) {
      const tier = fm.tier as Tier;
      tierCounts.set(tier, (tierCounts.get(tier) ?? 0) + 1);
      const category = categoryOf(fm);
      categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
      const layer = layerOf(fm.id);
      layerCounts.set(layer, (layerCounts.get(layer) ?? 0) + 1);
    }

    return { tierCounts, categoryCounts, layerCounts };
  }, [modes]);

  // Filters combine: a mode must satisfy every group that has a selection.
  const visible = useMemo(
    () =>
      modes.filter((fm) => {
        if (tiers.size > 0 && !tiers.has(fm.tier as Tier)) return false;
        if (categories.size > 0 && !categories.has(categoryOf(fm))) return false;
        if (layers.size > 0 && !layers.has(layerOf(fm.id))) return false;
        return true;
      }),
    [modes, tiers, categories, layers],
  );

  const groups = useMemo(
    () => groupChecksByPlanArea(modes, visible),
    [modes, visible],
  );

  /**
   * Functional update, not a copy of the current Set. Reading the Set from the
   * render closure loses an update when two filters are clicked in the same
   * tick: both start from the same "before" value and the second overwrites the
   * first, leaving a filter stuck on.
   */
  const toggle = <T,>(
    value: T,
    apply: (update: (current: Set<T>) => Set<T>) => void,
  ) => {
    apply((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearFilters = () => {
    setTiers(new Set());
    setCategories(new Set());
    setLayers(new Set());
  };

  const tier1 = counts.tierCounts.get(1) ?? 0;
  const tier2 = counts.tierCounts.get(2) ?? 0;
  const filtered = tiers.size > 0 || categories.size > 0 || layers.size > 0;

  return (
    <GlossaryProvider>
      <div className="tx">
        <header className="tx__header">
          <div>
            <Label mono>Reference · The 15 checks</Label>
            <h1 className="t-display-2">What this tool checks for</h1>
          </div>
          <div>
            <p className="tx__lede">
              {modes.length} checks. {tier1} are Tier 1, meaning a named federal
              audit records this exact cause producing a real dispute or dollar
              loss.
            </p>
            <p className="tx__sub">
              The remaining {tier2} are Tier 2:{' '}
              <Glossed>
                FEMP&rsquo;s own guidance warns about them, but no audit case is
                attached yet.
              </Glossed>
            </p>
          </div>
        </header>

        <div className="tx__body-grid">
          <nav className="tx__filters" aria-label="Filters">
            <div className="tx__filters-inner">
              <FilterGroup
                label="Tier"
                options={TIERS}
                selected={tiers}
                onToggle={(v) => toggle(v, setTiers)}
                counts={counts.tierCounts}
                renderOption={(v) => `Tier ${v}`}
              />
              <FilterGroup
                label="RRP category"
                options={CATEGORIES}
                selected={categories}
                onToggle={(v) => toggle(v, setCategories)}
                counts={counts.categoryCounts}
              />
              <FilterGroup
                label="How it is checked"
                options={LAYERS}
                selected={layers}
                onToggle={(v) => toggle(v, setLayers)}
                counts={counts.layerCounts}
                renderOption={(v) => LAYER_LABELS[v]}
              />

              <p className="tx__filter-state">
                {filtered
                  ? `Showing ${visible.length} of ${modes.length}`
                  : `Showing all ${modes.length}`}
              </p>

              {/* Muting is per browser and easy to forget about. It is stated
                  here, on the page that turns it back on, rather than only
                  inside the row it applies to. */}
              {mutedList.length > 0 ? (
                <p className="tx__muted-count">
                  {mutedList.length === 1
                    ? '1 check is muted in this browser'
                    : `${mutedList.length} checks are muted in this browser`}
                  : {mutedList.map((fm) => fm.plainTitle).join(' · ')}. Muted
                  checks still run; open one below to turn it back on.
                </p>
              ) : null}

              <div className="tx__built-from">
                <Label>Built from</Label>
                <ul className="tx__built-list">
                  {TAXONOMY.built_from.map((source, i) => (
                    <li key={i}>{source}</li>
                  ))}
                </ul>
              </div>
            </div>
          </nav>

          {/* The shell already provides the page's one main landmark; this
              is the content column beside the filters, not a second one. */}
          <div className="tx__list-wrap">
            {groups.length === 0 ? (
              <EmptyState
                heading="No check matches every filter"
                body="The filters combine, so a check has to satisfy all of them at once. Clearing them shows all fifteen again."
                action={
                  <Button variant="primary" marker onClick={clearFilters}>
                    Clear the filters
                  </Button>
                }
              />
            ) : (
              /* Grouped by the part of the plan they are about, the same way
                 the workspace groups findings. A reviewer who has met the
                 groups in a review meets the same ones here. */
              groups.map((group) => (
                <section key={group.area} className="tx__group">
                  <h2 className="tx__group-head">
                    <span className="tx__group-name">{group.label}</span>
                    <span className="tx__group-count">
                      {group.modes.length === group.total
                        ? `${group.total} ${group.total === 1 ? 'check' : 'checks'}`
                        : `${group.modes.length} of ${group.total}`}
                    </span>
                  </h2>
                  <ul className="tx__list">
                    {group.modes.map((fm) => (
                      <Row
                        key={fm.id}
                        fm={fm}
                        muted={muted[fm.id]}
                        onToggleMute={toggleMute}
                      />
                    ))}
                  </ul>
                </section>
              ))
            )}
            <p className="tx__scoring">
              {numberWord(tier1)} of these have a named federal audit behind
              them, where the same cause produced a documented dispute or dollar
              loss. {numberWord(tier2)} are things FEMP&rsquo;s own guidance
              warns about, where no audit case is attached yet. Ceiling tells you
              which is which on every finding.
            </p>
          </div>
        </div>
      </div>
    </GlossaryProvider>
  );
}
